import SmartBanner from './lib/smartbanner'
import './smartbanner.css'
import { config } from './config'
import type { i18n } from './i18n'
import { getUserPreferedTranslationTable } from './i18n'

export type MobilePlatform = 'ios' | 'android'
export type SmartBannerConfig = {
  festivalName: string
  appTitle: string
  appStoreId: string
  appleStoreUrl: string
  googleStoreUrl: string
  appIconUrl: string

  forcedPlatform?: MobilePlatform
  parentElementId?: string
  disableInteractions?: boolean
  forcedStarsCount?: number
}

const embeddingScriptElement: HTMLScriptElement | null = document.currentScript as HTMLScriptElement | null

function getPreviewConfig(): SmartBannerConfig | undefined {
  if (!embeddingScriptElement) {
    return undefined
  }
  const configAttr: string | null = embeddingScriptElement.getAttribute('preview-config')
  return configAttr ? JSON.parse(configAttr) : undefined
}

class ChapitoSmartBanner extends SmartBanner {
  get html(): string {
    return this.platform
      ? `
      <div class="chapito-smartbanner smartbanner--${this.platform ?? 'ios'} js_smartbanner">
        <a href="javascript:void(0);" class="smartbanner__exit js_smartbanner__exit" aria-label="${this.options.closeLabel ?? ''}"></a>
        <div class="smartbanner__icon" style="background-image: url(${this.icon});"></div>
        <div class="smartbanner__info__title">${this.options.title ?? ''}</div>
        <div class="smartbanner__info__festival-name">${this.options.festivalName ?? ''}</div>
        <a href="${this.buttonUrl}" target="_blank" class="smartbanner__info__price" rel="noopener" aria-label="${this.options.button ?? ''}">
          ${this.getStoreText}
        </a>
        <a href="${this.buttonUrl}" target="_blank" class="smartbanner__button js_smartbanner__button" rel="noopener" aria-label="${
          this.options.button ?? ''
        }"><span class="smartbanner__button__label">${this.options.button ?? ''}</span></a>
      </div>
    `
      : ''
  }
}

async function showBanner(): Promise<void> {
  const t: i18n = getUserPreferedTranslationTable()
  const usedConfig: SmartBannerConfig = getPreviewConfig() /* backoffice */ ?? config /* static build, or during dev. */

  // this is assuming there is no XSS in the translation tables and config
  const smartbanner = new ChapitoSmartBanner()
  smartbanner.platform = usedConfig.forcedPlatform ?? smartbanner.platform
  smartbanner._options = {
    enabledPlatforms: 'android,ios',
    festivalName: usedConfig.festivalName,
    button: t['button'],
    closeLabel: t['close-label'],
    storeApple: t['price-suffix-apple'],
    storeGoogle: t['price-suffix-google'],
    title: usedConfig.appTitle,
    iconApple: usedConfig.appIconUrl,
    iconGoogle: usedConfig.appIconUrl,
    buttonUrlApple: usedConfig.appleStoreUrl,
    buttonUrlGoogle: usedConfig.googleStoreUrl,
    disablePositioning: usedConfig.parentElementId ? 'true' : undefined,
  }

  if (usedConfig.forcedPlatform) {
    document.cookie = 'chapito_smartbanner_closed=0; path=/; samesite=lax'
  }
  if (!document.cookie.includes('chapito_smartbanner_closed=1')) {
    smartbanner.publish()
    const exitBtn = document.querySelector('.js_smartbanner__exit')
    if (exitBtn) {
      exitBtn.addEventListener('click', () => {
        document.cookie = 'chapito_smartbanner_closed=1; path=/; samesite=lax'
      })
    }
  }

  const smartBannerElement = document.querySelector('.chapito-smartbanner') as HTMLDivElement
  if (!smartBannerElement) {
    return
  }
  if (usedConfig.disableInteractions) {
    smartBannerElement.style.pointerEvents = 'none'
  }
  if (usedConfig.parentElementId) {
    document.getElementById(usedConfig.parentElementId)?.appendChild(smartBannerElement)
  }
}

// add an event as a way of showing the banner back up if it was
// closed, without reloading the script (mainly for backoffice uses)
document.addEventListener('chapito-smartbanner-show', () => {
  showBanner()
})
// kickstart the banner on script load
showBanner()
